import { Flex, Text, Checkbox, Button } from '@chakra-ui/react';
import { ShowSkipStop } from '../../components/ShowSkipStop';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRestartOutreachCandidatesMutation } from '@/store/query/mco-candidates';
import { useSendMessageToExtension } from '@/hooks/use-check-extension-installed';
import { KText } from 'ui-library/components';
import { useShowToast } from '@/hooks/useShowToast';

export const InmailCreditsExhausted = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const [isAcknowledged, setAcknowledge] = useState(false);
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();
  const { sendMessage } = useSendMessageToExtension();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 0,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      // After Restart success, inform ext to resume tasks
      sendMessage({
        messageTypeobj: {
          type: 'flow_error_reset',
          errors_reset: { li_inmail_limit_reached: true },
        },
      });
    } catch {
      /* noop */
    }
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-inmail-limit-reached"
    >
      <Text fontWeight="semiBold" fontSize="24px">
        LinkedIn - InMail credits exhausted
      </Text>
      <Text type="muted" color="#16161680">
        You have exhausted your InMail credits, preventing us from completing
        the current step. You can choose to perform the below actions.
      </Text>
      <Flex alignItems="start">
        <Checkbox
          mr="12px"
          isChecked={isAcknowledged}
          onChange={() => setAcknowledge((prev) => !prev)}
          data-test-id="flow-restart-action-inmail-limit-reached-checkbox"
        >
          I acknowledge that I have refilled my credits
        </Checkbox>
      </Flex>
      <Button
        disabled={!isAcknowledged}
        onClick={onRestart}
        isLoading={isLoading}
        data-test-id="flow-restart-action-inmail-limit-reached-cta"
      >
        Restart Flow
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const InmailPremiumUnavailable = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const [isAcknowledged, setAcknowledge] = useState(false);
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();
  const { sendMessage } = useSendMessageToExtension();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 0,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      // After Restart success, inform ext to resume tasks
      sendMessage({
        messageTypeobj: {
          type: 'flow_error_reset',
          errors_reset: { li_inmail_forbidden: true },
        },
      });
    } catch {
      /* noop */
    }
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-inmail-forbidden"
    >
      <KText size="md-medium" color="secondary-900">
        LinkedIn - Recruiter license not found
      </KText>
      <KText>
        {`To continue, a valid LinkedIn Recruiter account is needed for this step. You can choose to purchase the licence and restart the flow ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'or skip ahead to the next step or to stop the flow.'
            : 'or you can choose to stop the flow'
        }`}
      </KText>
      <Flex alignItems="start">
        <Checkbox
          mr="12px"
          isChecked={isAcknowledged}
          onChange={() => setAcknowledge((prev) => !prev)}
          data-test-id="flow-restart-action-inmail-forbidden-checkbox"
        >
          <KText>
            I acknowledge that I have renewed / bought the subscription
          </KText>
        </Checkbox>
      </Flex>
      <Button
        disabled={!isAcknowledged}
        onClick={onRestart}
        isLoading={isLoading}
        data-test-id="flow-restart-action-inmail-forbidden-cta"
      >
        Restart Flow
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const InmailRateLimit = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  const params = useParams();
  const [restartOutreach, { isLoading }] =
    useRestartOutreachCandidatesMutation();
  const toast = useShowToast();

  const onRestart = async () => {
    try {
      const candidates = [
        {
          candidate_id: restartDetails?.outreach_candidate?.candidate_id,
          delay: 1440,
        },
      ];
      const result = await restartOutreach({
        candidates,
        outreachId: params?.outreachId,
      });
      if (result?.data?.[0]?.errors?.length) {
        toast({
          title: 'Unable to restart the Flow',
          description: result?.data?.[0]?.errors?.[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      toast({
        title: 'Flow re-started successfully.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
    } catch {
      /* noop */
    }
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-inmail-rate-limited"
    >
      <Text fontWeight="semiBold" fontSize="24px">
        LinkedIn - Exceeded LinkedIn limits
      </Text>
      <Text type="muted" color="#16161680">
        You have reached the maximum number of InMails which you can send per
        day or some LinkedIn limits has been exceeded. You can choose to perform
        the below actions.
      </Text>
      <Button
        onClick={onRestart}
        isLoading={isLoading}
        data-test-id="flow-restart-action-inmail-rate-limited-cta"
      >
        Restart after 1 day
      </Button>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const InmailProfileNotFound = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-inmail-not-found"
    >
      <Text fontWeight="semiBold" fontSize="24px">
        LinkedIn - Profile URL not valid/found
      </Text>
      <Text type="muted" color="#16161680">
        {`LinkedIn profile URL is not valid/found, preventing us from completing the current step. ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'You can either skip ahead to the next step or decide to stop the flow for this candidate'
            : 'You can choose to stop the flow'
        }`}
      </Text>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};

export const InmailGeneralError = ({
  restartDetails = {},
  onStopOpen = () => {},
}) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="flow-restart-popover-inmail-error"
    >
      <Text fontWeight="semiBold" fontSize="24px">
        LinkedIn Error - InMail
      </Text>
      <Text type="muted" color="#16161680">
        {`LinkedIn has thrown an unexpected error, preventing us from completing
        the current step. ${
          restartDetails?.outreach_candidate?.step_to_restart?.skips_to_step
            ? 'You can either skip ahead to the next step or decide to stop the flow for this candidate'
            : 'You can choose to stop the flow'
        }`}
      </Text>
      <ShowSkipStop restartDetails={restartDetails} onStopOpen={onStopOpen} />
    </Flex>
  );
};
